import React, { useEffect } from 'react';
import { X, Award, Target, Calendar, Zap, TrendingUp, CheckCircle } from 'lucide-react';
import './LevelSystem.css';

const BadgeModal = ({ badge, onClose, t }) => {
  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  // Close modal on background click
  const handleBackgroundClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num?.toLocaleString() || '0';
  };

  const getRarityInfo = (rarity) => {
    switch (rarity) {
      case 'legendary':
        return { color: '#fbbf24', label: t ? t('legendary', 'Legendary') : 'Legendary', bg: 'linear-gradient(135deg, #d97706, #fbbf24)' };
      case 'epic':
        return { color: '#8b5cf6', label: t ? t('epic', 'Epic') : 'Epic', bg: 'linear-gradient(135deg, #7c3aed, #8b5cf6)' };
      case 'rare':
        return { color: '#3b82f6', label: t ? t('rare', 'Rare') : 'Rare', bg: 'linear-gradient(135deg, #2563eb, #3b82f6)' };
      default:
        return { color: '#71717a', label: t ? t('common', 'Common') : 'Common', bg: 'linear-gradient(135deg, #52525b, #71717a)' };
    }
  };

  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      'win_streak': t ? t('winStreak', 'Win Streak') : 'Win Streak',
      'filler': t ? t('filler', 'Filler') : 'Filler',
      'total_wins': t ? t('totalWins', 'Total Wins') : 'Total Wins',
      'points_winning': t ? t('pointsWinning', 'Points Won') : 'Points Won',
      'points_purchase': t ? t('pointsPurchase', 'Points Bought') : 'Points Bought',
      'multiplayer': 'Multiplayer',
      'battle_royale': 'Battle Royale',
      'multiplayer_wins': 'Multiplayer Wins',
      'battle_royale_wins': 'Battle Royale Wins'
    };

    return categoryNames[category] || category;
  };

  const getProgressTip = (category, remaining) => {
    const tips = {
      'win_streak': t ? t('winStreakTip', `Win ${remaining} more games in a row to unlock this badge!`) : `Win ${remaining} more games in a row to unlock this badge!`,
      'total_wins': t ? t('totalWinsTip', `${remaining} more victories needed. Keep playing!`) : `${remaining} more victories needed. Keep playing!`,
      'points_winning': t ? t('pointsWinningTip', `Earn ${formatNumber(remaining)} more points from wins.`) : `Earn ${formatNumber(remaining)} more points from wins.`,
      'points_purchase': t ? t('pointsPurchaseTip', `Purchase ${formatNumber(remaining)} more points to unlock.`) : `Purchase ${formatNumber(remaining)} more points to unlock.`,
      'filler': t ? t('fillerTip', `${remaining} more filler wins needed. Add fillers and hope they win!`) : `${remaining} more filler wins needed. Add fillers and hope they win!`,
      'multiplayer': t ? t('multiplayerTip', `Play ${remaining} more multiplayer games.`) : `Play ${remaining} more multiplayer games.`,
      'battle_royale': t ? t('battleRoyaleTip', `Join ${remaining} more Battle Royale matches.`) : `Join ${remaining} more Battle Royale matches.`,
      'multiplayer_wins': t ? t('multiplayerWinsTip', `Win ${remaining} more multiplayer tournaments.`) : `Win ${remaining} more multiplayer tournaments.`,
      'battle_royale_wins': t ? t('battleRoyaleWinsTip', `Win ${remaining} more BR matches to unlock.`) : `Win ${remaining} more BR matches to unlock.`
    };

    return tips[category] || (t ? t('genericTip', `${remaining} more to go. Keep it up!`) : `${remaining} more to go. Keep it up!`);
  };

  const isProgress = badge.isProgress || (!badge.unlockedAt && badge.progressPercentage !== undefined);
  const badgeName = badge.badgeName || badge.name;
  const expReward = badge.expReward || 0;
  const requirement = badge.requirement || 0;
  const rarityInfo = getRarityInfo(badge.rarity);
  const progressPercent = Math.min(100, badge.progressPercentage || 0);

  return (
    <div className="badge-modal-overlay" onClick={handleBackgroundClick}>
      <div className="badge-modal">
        {/* Close Button */}
        <button
          className="badge-modal-close"
          onClick={onClose}
          aria-label={t ? t('close', 'Close') : 'Close'}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="badge-modal-header">
          <div className="badge-modal-info">
            {/* Badge Icon with Rarity Glow */}
            <div
              className="badge-modal-icon"
              style={{
                filter: badge.rarity === 'legendary'
                  ? 'drop-shadow(0 0 20px rgba(251, 191, 36, 0.6))'
                  : badge.rarity === 'epic'
                  ? 'drop-shadow(0 0 15px rgba(139, 92, 246, 0.5))'
                  : badge.rarity === 'rare'
                  ? 'drop-shadow(0 0 12px rgba(59, 130, 246, 0.4))'
                  : 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.4))'
              }}
            >
              {badge.icon || '🏅'}
            </div>

            {/* Badge Name */}
            <h3 className="badge-modal-title">{badgeName}</h3>

            {/* Description */}
            <p className="badge-modal-description">{badge.description}</p>

            {/* Rarity Badge */}
            <div
              className={`badge-rarity ${badge.rarity || 'common'}`}
              style={{ background: rarityInfo.bg }}
            >
              {rarityInfo.label}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="badge-modal-stats">
          <div className="badge-stat">
            <div className="badge-stat-label">
              {t ? t('category', 'Category') : 'Category'}
            </div>
            <div className="badge-stat-value" style={{ fontSize: '13px' }}>
              {getCategoryDisplayName(badge.category)}
            </div>
          </div>

          <div className="badge-stat">
            <div className="badge-stat-label">
              {t ? t('expReward', 'EXP Reward') : 'EXP Reward'}
            </div>
            <div className="badge-stat-value" style={{ color: '#fbbf24' }}>
              +{formatNumber(expReward)}
            </div>
          </div>

          <div className="badge-stat">
            <div className="badge-stat-label">
              {t ? t('requirement', 'Required') : 'Required'}
            </div>
            <div className="badge-stat-value">
              {formatNumber(requirement)}
            </div>
          </div>

          {badge.unlockedAt && (
            <div className="badge-stat" style={{ gridColumn: 'span 2' }}>
              <div className="badge-stat-label">
                {t ? t('unlockedOn', 'Unlocked On') : 'Unlocked On'}
              </div>
              <div className="badge-stat-value" style={{ fontSize: '13px' }}>
                {formatDate(badge.unlockedAt)}
              </div>
            </div>
          )}
        </div>

        {/* Progress Section (for in-progress badges) */}
        {isProgress && (
          <div className="badge-progress-info">
            <div className="badge-progress-header">
              <div className="badge-progress-label">
                <Target className="w-4 h-4" />
                {t ? t('progress', 'Progress') : 'Progress'}
              </div>
              <div className="badge-progress-percentage">
                {Math.round(progressPercent)}%
              </div>
            </div>

            <div className="badge-progress-bar-container">
              <div
                className="badge-progress-bar-fill"
                style={{ width: `${progressPercent}%` }}
              />
            </div>

            <div className="badge-progress-text">
              <span style={{ color: '#a1a1aa' }}>
                {formatNumber(badge.currentProgress || 0)}
              </span>
              <span style={{ color: '#52525b' }}> / </span>
              <span style={{ color: '#fff' }}>
                {formatNumber(requirement)}
              </span>
              {badge.remaining > 0 && (
                <span style={{ color: '#fbbf24', marginLeft: '12px' }}>
                  ({formatNumber(badge.remaining)} {t ? t('remaining', 'left') : 'left'})
                </span>
              )}
            </div>
          </div>
        )}

        {/* Unlocked Badge Celebration */}
        {!isProgress && badge.unlockedAt && (
          <div className="badge-progress-info" style={{ textAlign: 'center' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              marginBottom: '16px'
            }}>
              <CheckCircle className="w-6 h-6" style={{ color: '#22c55e' }} />
              <span style={{
                color: '#22c55e',
                fontSize: '16px',
                fontWeight: 700
              }}>
                {t ? t('badgeUnlocked', 'Badge Unlocked!') : 'Badge Unlocked!'}
              </span>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '16px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  color: '#71717a',
                  fontSize: '12px',
                  marginBottom: '6px'
                }}>
                  <Calendar className="w-4 h-4" />
                  {t ? t('earnedOn', 'Earned On') : 'Earned On'}
                </div>
                <div style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>
                  {formatDate(badge.unlockedAt)}
                </div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  color: '#71717a',
                  fontSize: '12px',
                  marginBottom: '6px'
                }}>
                  <Zap className="w-4 h-4" />
                  {t ? t('expGained', 'EXP Gained') : 'EXP Gained'}
                </div>
                <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: '14px' }}>
                  +{formatNumber(expReward)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tips Section (for in-progress badges) */}
        {isProgress && badge.remaining > 0 && (
          <div className="badge-tips">
            <div className="badge-tips-header">
              <TrendingUp className="w-4 h-4" />
              {t ? t('tip', 'Tip') : 'Tip'}
            </div>
            <p style={{ margin: 0, lineHeight: 1.5 }}>
              {getProgressTip(badge.category, badge.remaining)}
            </p>
          </div>
        )}

        {/* Action Button */}
        <div className="badge-modal-action">
          <button
            className={`badge-modal-btn ${badge.rarity || 'common'}`}
            onClick={onClose}
          >
            {isProgress
              ? (t ? t('keepGoing', 'Keep Going!') : 'Keep Going!')
              : (t ? t('awesome', 'Awesome!') : 'Awesome!')
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeModal;
